import { z } from 'zod';

/**
 * Electric Load Configuration Schema
 *
 * Defines the building's electrical load profile.
 * Supports both hourly (8760) and 15-minute (35040) resolution.
 *
 * Backend preprocessing will interpolate hourly to 15-min using
 * facility-type-specific patterns for accurate demand charge modeling.
 */

// Valid load profile lengths
const HOURLY_LENGTH = 8760;
const QUARTER_HOURLY_LENGTH = 35040;

// Load profile array - accepts either resolution
const LoadProfileSchema = z.array(z.number().min(0, 'Load values cannot be negative'))
  .refine(
    (arr) => arr.length === HOURLY_LENGTH || arr.length === QUARTER_HOURLY_LENGTH,
    { message: `Load array must have ${HOURLY_LENGTH} values (hourly) or ${QUARTER_HOURLY_LENGTH} values (15-min)` }
  );

export const ElectricLoadSchema = z.object({
  // Required: load values in kW
  // Can be 8760 (hourly) or 35040 (15-minute intervals)
  // Backend interpolates hourly to 15-min using facility type pattern
  loads_kw: LoadProfileSchema,

  // Year for the load profile (affects leap year handling)
  year: z.number()
    .int('Year must be an integer')
    .min(2000, 'Year must be >= 2000')
    .max(2099, 'Year must be <= 2099')
    .optional(),

  // Critical load fraction for resilience calculations
  critical_load_fraction: z.number()
    .min(0, 'Critical load fraction must be >= 0')
    .max(1, 'Critical load fraction must be <= 1')
    .default(0.5),

  // Computed/display fields (not required for validation)
  annual_kwh: z.number().min(0).optional(),
  peak_kw: z.number().min(0).optional(),
});

/**
 * Determine the resolution of a load profile
 */
export const getLoadResolution = (loads_kw) => {
  if (!loads_kw) return null;
  if (loads_kw.length === HOURLY_LENGTH) return 'hourly';
  if (loads_kw.length === QUARTER_HOURLY_LENGTH) return '15-min';
  return 'invalid';
};

/**
 * Calculate load profile statistics
 */
export const calculateLoadStats = (loads_kw) => {
  if (!loads_kw || loads_kw.length === 0) {
    return { annual_kwh: 0, peak_kw: 0, average_kw: 0 };
  }

  const resolution = getLoadResolution(loads_kw);
  const hoursPerInterval = resolution === '15-min' ? 0.25 : 1;

  const sum = loads_kw.reduce((a, b) => a + b, 0);
  const peak = Math.max(...loads_kw);
  const average = sum / loads_kw.length;
  const annual_kwh = sum * hoursPerInterval;

  return { annual_kwh, peak_kw: peak, average_kw: average };
};

/**
 * Validate load profile for common issues
 */
export const validateLoadProfile = (load) => {
  const warnings = [];
  const errors = [];

  if (!load.loads_kw || load.loads_kw.length === 0) {
    errors.push('Load profile is required');
    return { valid: false, errors, warnings };
  }

  const stats = calculateLoadStats(load.loads_kw);

  // Check for all zeros
  if (stats.annual_kwh === 0) {
    errors.push('Load profile is all zeros - optimization requires non-zero load');
  }

  // Check for negative values
  const min = Math.min(...load.loads_kw);
  if (min < 0) {
    errors.push('Load profile contains negative values - this is invalid');
  }

  // Check for unreasonably high peaks
  if (stats.peak_kw > 100000) {
    warnings.push('Load profile has very high peak (>100MW) - verify this is correct');
  }

  // Check for suspicious patterns
  const uniqueValues = new Set(load.loads_kw).size;
  if (uniqueValues === 1 && load.loads_kw.length > 1) {
    warnings.push('Load profile has identical values for all intervals - this may be synthetic data');
  }

  return {
    valid: errors.length === 0,
    errors,
    warnings,
    stats,
  };
};

export default ElectricLoadSchema;
